/**
 * MEVIA - HR Individual Report Component
 * Shows detailed HR indicators for a single employee with export functionality
 * Includes explanations, conclusions, recommendations, and glossary
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  ArrowLeft, Download, Loader2, AlertTriangle, CheckCircle,
  TrendingUp, TrendingDown, Target, Heart, Shield, Activity,
  Users, UserX, Info, BookOpen, Lightbulb, FileText
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import IPSGauge from './IPSGauge';
import logger from '../../utils/logger';

// Indicator definitions with detailed explanations
const INDICATOR_DETAILS = {
  irr: {
    name: { es: 'Índice de Riesgo de Rotación', en: 'Rotation Risk Index' },
    shortName: 'IRR',
    icon: UserX,
    color: '#EF4444',
    invertedScale: true,
    description: {
      es: 'Mide la probabilidad de que el empleado abandone la organización en los próximos 12 meses.',
      en: 'Measures the probability that the employee will leave the organization in the next 12 months.'
    },
    interpretation: {
      es: {
        high: 'Alto riesgo de rotación. Se recomienda intervención inmediata para identificar y abordar las causas de insatisfacción.',
        medium: 'Riesgo moderado. Monitorear de cerca y considerar acciones preventivas.',
        low: 'Bajo riesgo. El empleado muestra señales de estabilidad y compromiso.'
      },
      en: {
        high: 'High rotation risk. Immediate intervention recommended to identify and address dissatisfaction causes.',
        medium: 'Moderate risk. Monitor closely and consider preventive actions.',
        low: 'Low risk. Employee shows signs of stability and commitment.'
      }
    },
    recommendations: {
      es: {
        high: ['Realizar entrevista de retención', 'Revisar compensación y beneficios', 'Evaluar carga de trabajo y ambiente laboral'],
        medium: ['Establecer plan de desarrollo profesional', 'Aumentar frecuencia de feedback', 'Considerar ajustes en responsabilidades'],
        low: ['Mantener comunicación abierta', 'Reconocer logros regularmente', 'Incluir en proyectos estratégicos']
      },
      en: {
        high: ['Conduct retention interview', 'Review compensation and benefits', 'Evaluate workload and work environment'],
        medium: ['Establish professional development plan', 'Increase feedback frequency', 'Consider responsibility adjustments'],
        low: ['Maintain open communication', 'Recognize achievements regularly', 'Include in strategic projects']
      }
    }
  },
  ico: {
    name: { es: 'Índice de Compromiso Organizacional', en: 'Organizational Commitment Index' },
    shortName: 'ICO',
    icon: Heart,
    color: '#3B82F6',
    invertedScale: false,
    description: {
      es: 'Evalúa el nivel de conexión emocional y compromiso del empleado con la misión y valores de la organización.',
      en: 'Evaluates the level of emotional connection and commitment of the employee to the organization\'s mission and values.'
    },
    interpretation: {
      es: {
        high: 'Alto compromiso. El empleado está altamente identificado con la organización.',
        medium: 'Compromiso moderado. Existe conexión pero hay oportunidades de mejora.',
        low: 'Bajo compromiso. El empleado puede sentirse desconectado de la organización.'
      },
      en: {
        high: 'High commitment. Employee is highly identified with the organization.',
        medium: 'Moderate commitment. Connection exists but there are improvement opportunities.',
        low: 'Low commitment. Employee may feel disconnected from the organization.'
      }
    },
    recommendations: {
      es: {
        high: ['Involucrar en iniciativas de cultura', 'Considerar para roles de liderazgo', 'Utilizar como embajador interno'],
        medium: ['Reforzar comunicación de visión y valores', 'Crear oportunidades de participación', 'Establecer metas significativas'],
        low: ['Explorar causas de desconexión', 'Revisar alineación persona-puesto', 'Considerar cambio de rol o proyecto']
      },
      en: {
        high: ['Involve in culture initiatives', 'Consider for leadership roles', 'Use as internal ambassador'],
        medium: ['Reinforce vision and values communication', 'Create participation opportunities', 'Establish meaningful goals'],
        low: ['Explore causes of disconnection', 'Review person-position alignment', 'Consider role or project change']
      }
    }
  },
  iap: {
    name: { es: 'Índice de Adecuación al Puesto', en: 'Job Fit Index' },
    shortName: 'IAP',
    icon: Target,
    color: '#8B5CF6',
    invertedScale: false,
    description: {
      es: 'Mide qué tan bien el perfil del empleado se ajusta a los requerimientos y responsabilidades de su puesto actual.',
      en: 'Measures how well the employee\'s profile fits the requirements and responsibilities of their current position.'
    },
    interpretation: {
      es: {
        high: 'Excelente ajuste al puesto. El empleado tiene las competencias ideales para su rol.',
        medium: 'Ajuste aceptable. Algunas áreas podrían beneficiarse de desarrollo.',
        low: 'Bajo ajuste. Considerar reasignación o capacitación intensiva.'
      },
      en: {
        high: 'Excellent job fit. Employee has ideal competencies for their role.',
        medium: 'Acceptable fit. Some areas could benefit from development.',
        low: 'Low fit. Consider reassignment or intensive training.'
      }
    },
    recommendations: {
      es: {
        high: ['Delegar responsabilidades adicionales', 'Considerar para promoción', 'Utilizar como mentor'],
        medium: ['Identificar brechas específicas', 'Crear plan de capacitación dirigido', 'Proporcionar coaching'],
        low: ['Evaluar posibles reasignaciones', 'Diseñar plan de desarrollo intensivo', 'Revisar descripción del puesto']
      },
      en: {
        high: ['Delegate additional responsibilities', 'Consider for promotion', 'Use as mentor'],
        medium: ['Identify specific gaps', 'Create targeted training plan', 'Provide coaching'],
        low: ['Evaluate possible reassignments', 'Design intensive development plan', 'Review job description']
      }
    }
  },
  ipc: {
    name: { es: 'Índice de Potencial de Crecimiento', en: 'Growth Potential Index' },
    shortName: 'IPC',
    icon: TrendingUp,
    color: '#22C55E',
    invertedScale: false,
    description: {
      es: 'Evalúa la capacidad del empleado para asumir mayores responsabilidades y desarrollarse profesionalmente.',
      en: 'Evaluates the employee\'s capacity to take on greater responsibilities and develop professionally.'
    },
    interpretation: {
      es: {
        high: 'Alto potencial. Candidato ideal para programas de desarrollo de talento.',
        medium: 'Potencial moderado. Con inversión adecuada puede crecer significativamente.',
        low: 'Potencial limitado en el rol actual. Explorar otras áreas de desarrollo.'
      },
      en: {
        high: 'High potential. Ideal candidate for talent development programs.',
        medium: 'Moderate potential. With adequate investment can grow significantly.',
        low: 'Limited potential in current role. Explore other development areas.'
      }
    },
    recommendations: {
      es: {
        high: ['Incluir en programa de alto potencial', 'Asignar proyectos desafiantes', 'Crear plan de sucesión'],
        medium: ['Identificar áreas de interés', 'Proporcionar mentoría', 'Establecer metas de desarrollo'],
        low: ['Enfocarse en dominio del rol actual', 'Explorar movimientos laterales', 'Reconocer contribuciones actuales']
      },
      en: {
        high: ['Include in high-potential program', 'Assign challenging projects', 'Create succession plan'],
        medium: ['Identify areas of interest', 'Provide mentorship', 'Establish development goals'],
        low: ['Focus on mastery of current role', 'Explore lateral moves', 'Recognize current contributions']
      }
    }
  },
  icf: {
    name: { es: 'Índice de Ajuste Cultural', en: 'Cultural Fit Index' },
    shortName: 'ICF',
    icon: Users,
    color: '#6366F1',
    invertedScale: false,
    description: {
      es: 'Mide la compatibilidad entre los valores y estilo de trabajo del empleado con la cultura organizacional.',
      en: 'Measures the compatibility between the employee\'s values and work style with the organizational culture.'
    },
    interpretation: {
      es: {
        high: 'Excelente ajuste cultural. El empleado encaja naturalmente en la organización.',
        medium: 'Ajuste cultural aceptable. Algunas diferencias que pueden gestionarse.',
        low: 'Desajuste cultural. Puede generar fricción o insatisfacción.'
      },
      en: {
        high: 'Excellent cultural fit. Employee naturally fits in the organization.',
        medium: 'Acceptable cultural fit. Some differences that can be managed.',
        low: 'Cultural mismatch. May generate friction or dissatisfaction.'
      }
    },
    recommendations: {
      es: {
        high: ['Involucrar en onboarding de nuevos empleados', 'Incluir en comités culturales', 'Promover como ejemplo'],
        medium: ['Facilitar integración con equipos', 'Clarificar expectativas culturales', 'Proporcionar feedback constructivo'],
        low: ['Evaluar fuentes de desajuste', 'Considerar equipos más compatibles', 'Revisar fit a largo plazo']
      },
      en: {
        high: ['Involve in new employee onboarding', 'Include in cultural committees', 'Promote as example'],
        medium: ['Facilitate team integration', 'Clarify cultural expectations', 'Provide constructive feedback'],
        low: ['Evaluate sources of mismatch', 'Consider more compatible teams', 'Review long-term fit']
      }
    }
  },
  ipe: {
    name: { es: 'Índice de Productividad Esperada', en: 'Expected Productivity Index' },
    shortName: 'IPE',
    icon: Activity,
    color: '#06B6D4',
    invertedScale: false,
    description: {
      es: 'Predice el nivel de productividad esperado basado en el perfil del empleado y las condiciones actuales.',
      en: 'Predicts the expected productivity level based on the employee\'s profile and current conditions.'
    },
    interpretation: {
      es: {
        high: 'Alta productividad esperada. Condiciones óptimas para rendimiento.',
        medium: 'Productividad moderada. Hay factores que pueden optimizarse.',
        low: 'Productividad limitada. Identificar y remover obstáculos.'
      },
      en: {
        high: 'High expected productivity. Optimal conditions for performance.',
        medium: 'Moderate productivity. There are factors that can be optimized.',
        low: 'Limited productivity. Identify and remove obstacles.'
      }
    },
    recommendations: {
      es: {
        high: ['Establecer metas ambiciosas', 'Proporcionar autonomía', 'Reconocer alto desempeño'],
        medium: ['Identificar cuellos de botella', 'Optimizar herramientas y procesos', 'Proporcionar recursos adicionales'],
        low: ['Realizar diagnóstico de obstáculos', 'Revisar carga de trabajo', 'Considerar capacitación técnica']
      },
      en: {
        high: ['Set ambitious goals', 'Provide autonomy', 'Recognize high performance'],
        medium: ['Identify bottlenecks', 'Optimize tools and processes', 'Provide additional resources'],
        low: ['Conduct obstacle diagnosis', 'Review workload', 'Consider technical training']
      }
    }
  },
  ilo: {
    name: { es: 'Índice de Lealtad Organizacional', en: 'Organizational Loyalty Index' },
    shortName: 'ILO',
    icon: Shield,
    color: '#14B8A6',
    invertedScale: false,
    description: {
      es: 'Evalúa la probabilidad de que el empleado permanezca leal a la organización a largo plazo.',
      en: 'Evaluates the likelihood that the employee will remain loyal to the organization long-term.'
    },
    interpretation: {
      es: {
        high: 'Alta lealtad. El empleado tiene fuerte vínculo con la organización.',
        medium: 'Lealtad moderada. El vínculo existe pero puede fortalecerse.',
        low: 'Baja lealtad. Riesgo de que el empleado considere otras opciones.'
      },
      en: {
        high: 'High loyalty. Employee has strong bond with the organization.',
        medium: 'Moderate loyalty. Bond exists but can be strengthened.',
        low: 'Low loyalty. Risk that employee may consider other options.'
      }
    },
    recommendations: {
      es: {
        high: ['Invertir en desarrollo a largo plazo', 'Incluir en planes estratégicos', 'Fortalecer relación'],
        medium: ['Aumentar sentido de pertenencia', 'Crear vínculos más profundos', 'Mejorar experiencia del empleado'],
        low: ['Identificar causas de desapego', 'Evaluar competitividad de la oferta', 'Revisar oportunidades de crecimiento']
      },
      en: {
        high: ['Invest in long-term development', 'Include in strategic plans', 'Strengthen relationship'],
        medium: ['Increase sense of belonging', 'Create deeper bonds', 'Improve employee experience'],
        low: ['Identify causes of detachment', 'Evaluate offer competitiveness', 'Review growth opportunities']
      }
    }
  }
};

// Glossary of terms
const GLOSSARY = {
  es: [
    { term: 'Capital Humano', definition: 'Conocimientos, habilidades y competencias que posee una persona.' },
    { term: 'Capital Social', definition: 'Red de relaciones y conexiones profesionales y personales.' },
    { term: 'Capital Cultural', definition: 'Valores, creencias y normas que influyen en el comportamiento.' },
    { term: 'Capital Psicológico', definition: 'Recursos mentales como resiliencia, optimismo y autoeficacia.' },
    { term: 'Dimensiones MEVIA', definition: 'Las 10 áreas de capital que evalúa el sistema: Físico, Humano, Manufacturado, Financiero, Natural, Cultural, Social, Conocimiento, Psicológico y Propósito.' },
    { term: 'Eneagrama', definition: 'Sistema de clasificación de personalidad en 9 tipos distintos.' },
    { term: 'Correlación', definition: 'Relación estadística entre dos variables que indica cómo se mueven juntas.' }
  ],
  en: [
    { term: 'Human Capital', definition: 'Knowledge, skills, and competencies that a person possesses.' },
    { term: 'Social Capital', definition: 'Network of professional and personal relationships and connections.' },
    { term: 'Cultural Capital', definition: 'Values, beliefs, and norms that influence behavior.' },
    { term: 'Psychological Capital', definition: 'Mental resources such as resilience, optimism, and self-efficacy.' },
    { term: 'MEVIA Dimensions', definition: 'The 10 capital areas evaluated by the system: Physical, Human, Manufactured, Financial, Natural, Cultural, Social, Knowledge, Psychological, and Purpose.' },
    { term: 'Enneagram', definition: 'Personality classification system with 9 distinct types.' },
    { term: 'Correlation', definition: 'Statistical relationship between two variables indicating how they move together.' }
  ]
};

// Get level from value
const getLevel = (key, value) => {
  const config = INDICATOR_DETAILS[key];
  if (!config) return 'medium';
  
  if (config.invertedScale) {
    if (value >= 60) return 'high';
    if (value >= 40) return 'medium';
    return 'low';
  } else {
    if (value >= 70) return 'high';
    if (value >= 40) return 'medium';
    return 'low';
  }
};

// Get color from level
const getLevelColor = (key, value) => {
  const config = INDICATOR_DETAILS[key];
  if (!config) return '#6B7280';
  
  const level = getLevel(key, value);
  if (config.invertedScale) {
    return level === 'low' ? '#22C55E' : level === 'medium' ? '#F59E0B' : '#EF4444';
  } else {
    return level === 'high' ? '#22C55E' : level === 'medium' ? '#F59E0B' : '#EF4444';
  }
};

// Individual Indicator Card with details
const DetailedIndicatorCard = ({ indicatorKey, value, drivers = [] }) => {
  const { language } = useLanguage();
  const [showDrivers, setShowDrivers] = useState(false);
  const config = INDICATOR_DETAILS[indicatorKey];
  if (!config) return null;
  
  const Icon = config.icon;
  const level = getLevel(indicatorKey, value);
  const color = getLevelColor(indicatorKey, value);
  const interpretation = config.interpretation[language][level];
  const recommendations = config.recommendations[language][level];
  
  return (
    <Card className="border-l-4" style={{ borderLeftColor: color }}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color }} />
            <CardTitle className="text-sm">{config.name[language]}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold" style={{ color }}>{value}%</span>
          </div>
        </div>
        <CardDescription className="text-xs">{config.description[language]}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Progress Bar */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all"
            style={{ width: `${value}%`, backgroundColor: color }}
          />
        </div>
        
        {/* Interpretation */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs font-medium text-gray-700 mb-1">
            {language === 'es' ? 'Interpretación:' : 'Interpretation:'}
          </p>
          <p className="text-xs text-gray-600">{interpretation}</p>
        </div>
        
        {/* Recommendations */}
        <div>
          <p className="text-xs font-medium text-gray-700 mb-1">
            {language === 'es' ? 'Recomendaciones:' : 'Recommendations:'}
          </p>
          <ul className="text-xs text-gray-600 space-y-1">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-1">
                <span className="text-teal-600">•</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Drivers — expandable narrative */}
        {drivers && drivers.length > 0 && (
          <div className="mt-1">
            <button 
              onClick={() => setShowDrivers(!showDrivers)}
              className="text-xs font-medium text-teal-700 hover:text-teal-900 flex items-center gap-1 cursor-pointer"
              data-testid={`drivers-toggle-${indicatorKey}`}
            >
              <Info className="h-3 w-3" />
              {language === 'es' ? '¿Por que este resultado?' : 'Why this result?'}
              <span className="text-[10px]">{showDrivers ? '▲' : '▼'}</span>
            </button>
            {showDrivers && (
              <div className="mt-2 space-y-2 pl-1 border-l-2 border-gray-200" data-testid={`drivers-list-${indicatorKey}`}>
                {drivers.map((d, i) => {
                  const effectIcon = d.effect === 'green' ? '●' : d.effect === 'red' ? '●' : '●';
                  const effectColor = d.effect === 'green' ? '#22c55e' : d.effect === 'red' ? '#ef4444' : '#9ca3af';
                  const signalText = typeof d.signal === 'object' ? (d.signal[language] || d.signal.es) : d.signal;
                  const detailText = typeof d.detail === 'object' ? (d.detail[language] || d.detail.es) : d.detail;
                  return (
                    <div key={i} className="ml-2">
                      <div className="flex items-center gap-1.5">
                        <span style={{ color: effectColor, fontSize: '8px' }}>{effectIcon}</span>
                        <span className="text-xs font-medium text-gray-700">{signalText}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 ml-3.5">{detailText}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
const HRIndividualReport = ({ 
  employee, 
  assessmentId, 
  enneagramAssessmentId,
  source = 'dimensions', // 'dimensions', 'enneagram', or 'both'
  onBack,
  indicatorsToShow = ['irr', 'ico', 'iap', 'ipc', 'icf', 'ipe', 'ilo']
}) => {
  const { language } = useLanguage();
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [indicators, setIndicators] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadIndicators();
  }, [assessmentId, enneagramAssessmentId, source]);

  const loadIndicators = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let res;
      
      if (source === 'enneagram' && enneagramAssessmentId) {
        res = await axios.get(`/v2/hr-indicators/enneagram/${enneagramAssessmentId}`);
      } else if (assessmentId) {
        res = await axios.get(`/v2/hr-indicators/${assessmentId}`);
      } else {
        setError(language === 'es' ? 'No hay ID de evaluación disponible' : 'No assessment ID available');
        setLoading(false);
        return;
      }
      
      if (res.data.success) {
        setIndicators(res.data.data);
      } else {
        setError(res.data.message || 'Error loading indicators');
      }
    } catch (err) {
      logger.error('Error loading indicators:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      // Add pages if content is too long
      let heightLeft = pdfHeight;
      let position = 0;
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`HR_Report_${employee?.nombre || 'Employee'}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(language === 'es' ? 'Reporte exportado exitosamente' : 'Report exported successfully');
    } catch (err) {
      logger.error('Error exporting PDF:', err);
      toast.error(language === 'es' ? 'Error al exportar PDF' : 'Error exporting PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        <span className="ml-2 text-gray-600">
          {language === 'es' ? 'Cargando reporte...' : 'Loading report...'}
        </span>
      </div>
    );
  }

  if (error || !indicators) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Volver' : 'Back'}
        </Button>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-6 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700">{error || (language === 'es' ? 'No se pudieron cargar los indicadores' : 'Could not load indicators')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normalize indicator values - handle both formats:
  // Format 1: {irr: 20, ico: 42, ...} (simple numbers)
  // Format 2: {irr: {value: 20, level: "BAJO", ...}, ...} (objects with details)
  const rawIndicators = indicators.indicators || {};
  const indicatorValues = {};
  const indicatorLevels = {};
  
  for (const key of Object.keys(rawIndicators)) {
    const val = rawIndicators[key];
    if (typeof val === 'object' && val !== null && 'value' in val) {
      // Format 2: object with value property
      indicatorValues[key] = val.value;
      indicatorLevels[key] = val.level;
    } else {
      // Format 1: simple number
      indicatorValues[key] = val;
      indicatorLevels[key] = indicators.levels?.[`${key}_level`];
    }
  }
  
  // Get IPS data from the response
  const ipsData = indicators.ips;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Volver' : 'Back'}
        </Button>
        <Button onClick={exportToPDF} disabled={exporting}>
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {language === 'es' ? 'Exportar PDF' : 'Export PDF'}
        </Button>
      </div>

      {/* Report Content */}
      <div ref={reportRef} className="space-y-6 bg-white p-6 rounded-lg">
        {/* Title Section */}
        <div className="text-center border-b pb-4">
          <h1 className="text-2xl font-bold text-gray-800">
            {language === 'es' ? 'Reporte Individual de Indicadores de RH' : 'Individual HR Indicators Report'}
          </h1>
          <p className="text-lg text-teal-600 mt-1">{employee?.nombre || 'Employee'}</p>
          <p className="text-sm text-gray-500">{employee?.email}</p>
          <p className="text-xs text-gray-400 mt-2">
            {language === 'es' ? 'Generado el' : 'Generated on'}: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* IPS Gauge - Índice de Permanencia Saludable */}
        {ipsData && (
          <IPSGauge
            ips={ipsData.score}
            ipsLevel={ipsData.level}
            levelColor={ipsData.color}
            description={language === 'es' ? ipsData.description_es : ipsData.description_en}
            recommendedAction={language === 'es' ? ipsData.action_es : ipsData.action_en}
            breakdown={ipsData.breakdown}
            showBreakdown={true}
            size="large"
            showCard={true}
            title={language === 'es' ? 'Índice de Permanencia Saludable (IPS)' : 'Healthy Retention Index (IPS)'}
            subtitle={language === 'es' 
              ? 'Indicador compuesto que mide la salud de la permanencia del empleado'
              : 'Composite indicator measuring employee retention health'}
          />
        )}

        {/* Executive Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-teal-600" />
              {language === 'es' ? 'Resumen Ejecutivo' : 'Executive Summary'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              {language === 'es' 
                ? `Este reporte presenta el análisis de ${indicatorsToShow.length} indicadores clave de recursos humanos para ${employee?.nombre || 'el empleado'}. Los indicadores fueron calculados basándose en ${source === 'enneagram' ? 'el perfil de Eneagrama' : source === 'both' ? 'la evaluación de Dimensiones y el perfil de Eneagrama' : 'la evaluación de las 10 Dimensiones de Capital'}. El análisis proporciona una visión integral del potencial, riesgos y oportunidades de desarrollo del colaborador.`
                : `This report presents the analysis of ${indicatorsToShow.length} key human resources indicators for ${employee?.nombre || 'the employee'}. The indicators were calculated based on ${source === 'enneagram' ? 'the Enneagram profile' : source === 'both' ? 'the Dimensions assessment and Enneagram profile' : 'the 10 Capital Dimensions assessment'}. The analysis provides a comprehensive view of the collaborator's potential, risks, and development opportunities.`
              }
            </p>
          </CardContent>
        </Card>

        {/* Detailed Indicators */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-teal-600" />
            {language === 'es' ? 'Indicadores Detallados' : 'Detailed Indicators'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {indicatorsToShow.map(key => {
              const value = indicatorValues[key];
              if (value === undefined) return null;
              return (
                <DetailedIndicatorCard 
                  key={key}
                  indicatorKey={key}
                  value={value}
                  drivers={indicators.drivers?.[key] || []}
                />
              );
            })}
          </div>
        </div>

        {/* Red Flags Section */}
        {indicators.red_flags && indicators.red_flags.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base text-orange-700">
                <AlertTriangle className="h-5 w-5" />
                {language === 'es' ? 'Alertas Detectadas' : 'Detected Alerts'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {indicators.red_flags.map((flag, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-orange-700">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{flag.alert_type?.replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* General Conclusions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lightbulb className="h-5 w-5 text-teal-600" />
              {language === 'es' ? 'Conclusiones Generales' : 'General Conclusions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Strengths */}
            <div>
              <p className="text-sm font-medium text-green-700 mb-1">
                {language === 'es' ? 'Fortalezas Identificadas:' : 'Identified Strengths:'}
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                {indicatorsToShow
                  .filter(key => {
                    const val = indicatorValues[key];
                    const config = INDICATOR_DETAILS[key];
                    return config?.invertedScale ? val < 40 : val >= 60;
                  })
                  .map(key => (
                    <li key={key} className="flex items-start gap-1">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {INDICATOR_DETAILS[key]?.name[language]}
                    </li>
                  ))
                }
                {indicatorsToShow.filter(key => {
                  const val = indicatorValues[key];
                  const config = INDICATOR_DETAILS[key];
                  return config?.invertedScale ? val < 40 : val >= 60;
                }).length === 0 && (
                  <li className="text-gray-500 italic">
                    {language === 'es' ? 'No se identificaron fortalezas destacadas' : 'No outstanding strengths identified'}
                  </li>
                )}
              </ul>
            </div>
            
            {/* Areas for Improvement */}
            <div>
              <p className="text-sm font-medium text-orange-700 mb-1">
                {language === 'es' ? 'Áreas de Oportunidad:' : 'Areas for Improvement:'}
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                {indicatorsToShow
                  .filter(key => {
                    const val = indicatorValues[key];
                    const config = INDICATOR_DETAILS[key];
                    return config?.invertedScale ? val >= 60 : val < 40;
                  })
                  .map(key => (
                    <li key={key} className="flex items-start gap-1">
                      <TrendingDown className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      {INDICATOR_DETAILS[key]?.name[language]}
                    </li>
                  ))
                }
                {indicatorsToShow.filter(key => {
                  const val = indicatorValues[key];
                  const config = INDICATOR_DETAILS[key];
                  return config?.invertedScale ? val >= 60 : val < 40;
                }).length === 0 && (
                  <li className="text-gray-500 italic">
                    {language === 'es' ? 'No se identificaron áreas críticas de mejora' : 'No critical improvement areas identified'}
                  </li>
                )}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Glossary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-5 w-5 text-teal-600" />
              {language === 'es' ? 'Glosario de Términos' : 'Glossary of Terms'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {GLOSSARY[language].map((item, idx) => (
                <div key={idx} className="text-sm">
                  <span className="font-medium text-gray-700">{item.term}:</span>
                  <span className="text-gray-600 ml-1">{item.definition}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pt-4 border-t">
          <p>MEVIA - Sistema de Evaluación de Capital Organizacional</p>
          <p>{language === 'es' ? 'Reporte generado automáticamente' : 'Automatically generated report'}</p>
        </div>
      </div>
    </div>
  );
};

export default HRIndividualReport;
